import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Row, Col, Card, Button, Form, Spinner } from 'react-bootstrap';
import { FiDownload, FiTrendingUp, FiUsers, FiGlobe, FiPackage, FiCalendar } from 'react-icons/fi';
import { useAuth } from '../../services/check_login';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Analytics = ({ pageTitle }) => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Permissions
  const permissions = currentUser?.role?.permissions || {};
  const canViewAnalytics = permissions.canViewAnalytics || currentUser?.isAdmin;
  const canExportReports = permissions.canExportReports || currentUser?.isAdmin;
  const canManageCampaigns = permissions.canManageCampaigns || currentUser?.isAdmin;

  useEffect(() => {
    document.title = pageTitle || 'Analytics - Content Craft';
    if (canViewAnalytics) {
      fetchAnalytics();
    }
  }, [pageTitle, startDate, endDate]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();

      const response = await axios.get('/api/analytics/overview', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });

      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format = 'csv') => {
    if (!canExportReports) return;

    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const params = { format };
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();

      const response = await axios.get('/api/analytics/export', {
        params,
        headers: { Authorization: `Bearer ${token}` },
        responseType: format === 'csv' ? 'blob' : 'json'
      });

      if (format === 'csv') {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `analytics-${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(response.data, null, 2));
        const downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute("href", dataStr);
        downloadAnchorNode.setAttribute("download", `analytics-${Date.now()}.json`);
        document.body.appendChild(downloadAnchorNode);
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Error exporting data');
    } finally {
      setExporting(false);
    }
  };

  if (!canViewAnalytics) {
    return (
      <Container className="mt-4">
        <Card>
          <Card.Body className="text-center p-5">
            <h4>Access Denied</h4>
            <p>You don't have permission to view analytics.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (!analytics) return null;

  // Chart data
  const creativesByTypeData = {
    labels: analytics.creativesByType.map(item => item._id || 'Unknown'),
    datasets: [{
      label: 'Creatives by Type',
      data: analytics.creativesByType.map(item => item.count),
      backgroundColor: ['#3cbec6', '#28a745', '#ffc107', '#dc3545', '#17a2b8']
    }]
  };

  const trendData = {
    labels: analytics.creationTrends.map(item => item._id),
    datasets: [{
      label: 'Daily Creatives',
      data: analytics.creationTrends.map(item => item.count),
      borderColor: '#3cbec6',
      backgroundColor: 'rgba(0, 123, 255, 0.1)',
      tension: 0.4
    }]
  };

  const languageData = {
    labels: analytics.languageUsage.map(item => item._id),
    datasets: [{
      label: 'Language Usage',
      data: analytics.languageUsage.map(item => item.count),
      backgroundColor: [
        '#3cbec6', '#28a745', '#ffc107', '#dc3545', '#17a2b8',
        '#6610f2', '#e83e8c', '#fd7e14', '#20c997', '#6c757d'
      ]
    }]
  };

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <h2 className="mb-0">Analytics Dashboard</h2>
            <div className="d-flex flex-wrap gap-2">
              <DatePicker
                selected={startDate}
                onChange={setStartDate}
                placeholderText="Start Date"
                className="form-control"
                dateFormat="dd/MM/yyyy"
                isClearable
              />
              <DatePicker
                selected={endDate}
                onChange={setEndDate}
                placeholderText="End Date"
                className="form-control"
                dateFormat="dd/MM/yyyy"
                minDate={startDate}
                isClearable
              />
              {canExportReports && (
                <>
                  <Button 
                    variant="success"
                    onClick={() => handleExport('csv')}
                    disabled={exporting}
                  >
                    <FiDownload /> Export CSV
                  </Button>
                  <Button 
                    variant="info"
                    onClick={() => handleExport('json')}
                    disabled={exporting}
                  >
                    <FiDownload /> Export JSON
                  </Button>
                </>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Overview Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <FiPackage size={32} className="text-primary mb-2" />
              <h3>{analytics.overview.totalCreatives}</h3>
              <p className="text-muted">Total Creatives</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <FiUsers size={32} className="text-success mb-2" />
              <h3>{analytics.overview.totalUsers}</h3>
              <p className="text-muted">Total Users</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <FiGlobe size={32} className="text-warning mb-2" />
              <h3>{analytics.overview.totalLanguages}</h3>
              <p className="text-muted">Active Languages</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <FiTrendingUp size={32} className="text-info mb-2" />
              <h3>{analytics.versionStats.avgVersions.toFixed(1)}</h3>
              <p className="text-muted">Avg Versions</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Campaign Overview */}
      {canManageCampaigns && (
        <Row className="mb-4">
          <Col md={12}>
            <Card>
              <Card.Header><strong><FiCalendar /> Campaign Overview</strong></Card.Header>
              <Card.Body>
                <Row>
                  <Col className="text-center mb-3">
                    <h3>{analytics.overview.totalCampaigns}</h3>
                    <p className="text-muted">Total</p>
                  </Col>
                  {['Planned', 'Active', 'Completed', 'On Hold', 'Cancelled'].map((status) => {
                    const entry = analytics.campaignsByStatus.find((s) => s._id === status);
                    return (
                      <Col className="text-center mb-3" key={status}>
                        <h3>{entry ? entry.count : 0}</h3>
                        <p className="text-muted">{status}</p>
                      </Col>
                    );
                  })}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Charts */}
      <Row className="mb-4">
        <Col md={8}>
          <Card>
            <Card.Header><strong>Creation Trends (Last 30 Days)</strong></Card.Header>
            <Card.Body>
              <Line data={trendData} options={{ responsive: true, maintainAspectRatio: true }} />
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Header><strong>Creatives by Type</strong></Card.Header>
            <Card.Body>
              <Doughnut data={creativesByTypeData} options={{ responsive: true, maintainAspectRatio: true }} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header><strong>Top Creators</strong></Card.Header>
            <Card.Body>
              <Bar 
                data={{
                  labels: analytics.topCreators.map(item => item._id),
                  datasets: [{
                    label: 'Creatives Created',
                    data: analytics.topCreators.map(item => item.count),
                    backgroundColor: '#3cbec6'
                  }]
                }}
                options={{ responsive: true, maintainAspectRatio: true, indexAxis: 'y' }}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header><strong>Language Usage</strong></Card.Header>
            <Card.Body>
              <Bar 
                data={{
                  labels: analytics.languageUsage.map(item => item._id),
                  datasets: [{
                    label: 'Usage Count',
                    data: analytics.languageUsage.map(item => item.count),
                    backgroundColor: '#28a745'
                  }]
                }}
                options={{ responsive: true, maintainAspectRatio: true }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Status Distribution */}
      {analytics.creativesByStatus.length > 0 && (
        <Row className="mb-4">
          <Col md={12}>
            <Card>
              <Card.Header><strong>Content by Status</strong></Card.Header>
              <Card.Body>
                <Bar 
                  data={{
                    labels: analytics.creativesByStatus.map(item => item._id),
                    datasets: [{
                      label: 'Count',
                      data: analytics.creativesByStatus.map(item => item.count),
                      backgroundColor: '#17a2b8'
                    }]
                  }}
                  options={{ responsive: true, maintainAspectRatio: true }}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Campaign Types */}
      {analytics.creativesByCampaignType.length > 0 && (
        <Row className="mb-4">
          <Col md={12}>
            <Card>
              <Card.Header><strong>Campaign Types Distribution</strong></Card.Header>
              <Card.Body>
                <Bar 
                  data={{
                    labels: analytics.creativesByCampaignType.map(item => item._id || 'Unknown'),
                    datasets: [{
                      label: 'Count',
                      data: analytics.creativesByCampaignType.map(item => item.count),
                      backgroundColor: '#ffc107'
                    }]
                  }}
                  options={{ responsive: true, maintainAspectRatio: true }}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default Analytics;
